/**
 * Product Composable
 */

import type { GalleryProps } from '~/components/mol/Gallery.vue';
import { ProductAvailability, type PageProductSummaryFragment, type Product, type ProductPreviewContent } from '~/graphql/generated';
import type { BikeDetailInfo } from '~/@types/product';

export const useBikeDetail = (product: Partial<Product> | Partial<ProductPreviewContent> | undefined | null) => {
    if (!product || !isProductBike(product)) return;
    const appStore = useAppStore();
    const { metric, imperial } = storeToRefs(appStore);
    const { t, w, s } = useLocales();
    const getWeigthValue = (product: Partial<Product> | Partial<ProductPreviewContent>) => {
        const metricWeight = product.weight?.metric ? `${product.weight.metric}` : '';
        const imperialWeight = product.weight?.imperial ? `${product.weight.imperial}` : '';
        if (metric.value && imperial.value) {
            return `${w(metricWeight)} (${w(imperialWeight, imperial.value)})`;
        } else if (imperial.value) {
            return w(imperialWeight, imperial.value);
        }
        return w(metricWeight);
    };
    const getSizeValue = (product: Partial<Product> | Partial<ProductPreviewContent>) => {
        const metricSize = s([product.min_height?.metric, product.max_height?.metric]);
        const imperialSize = s([product.min_height?.imperial, product.max_height?.imperial], imperial.value);
        if (metric.value && imperial.value) {
            return `${metricSize} (${imperialSize})`;
        } else if (imperial.value) {
            return imperialSize;
        }
        return metricSize;
    };

    const bikeDetail: BikeDetailInfo = {
        wheels: {
            label: t('bikeDetail.bike'),
            value: `${product.wheels?.toString()}″`,
        },

        weight: {
            label: t('bikeDetail.weight'),
            value: getWeigthValue(product),
        },

        features: {
            label: t('bikeDetail.features'),
            value: product.features || '',
        },

        age: {
            label: t('bikeDetail.age'),
            value: `${product.min_age} - ${product.max_age} ${t('bikeDetail.years')}`,
        },

        size: {
            label: t('bikeDetail.height'),
            value: getSizeValue(product),
        },
    };

    return bikeDetail;
};

export const useProductAvailibility = (variant: Ref<CMSProductVariant | undefined>, notifyFormId: Ref<string | null | undefined>) => {
    const { t, te } = useLocales();

    const weekDifference = computed(() => {
        return variant.value?.availabilityDate ? Math.floor(dateDayDifference(new Date(), variant.value.availabilityDate) / 7) : -1;
    });

    const availability = computed(() => {
        if (!variant.value) return 'outOfStock';

        const { availabilityState, notifyMe } = variant.value;

        if (availabilityState === ProductAvailability.Available) return 'available';
        if (availabilityState === ProductAvailability.NotInThisRegion) return 'notInThisRegion';
        if (weekDifference.value >= 0) return 'commingSoon';

        if (notifyMe) return notifyFormId.value ? 'notifyMe' : 'outOfStock';

        return 'endOfLife';
    });

    const availabilityInfoText = computed(() => {
        if (availability.value === 'available') return;

        // If a ".status" translation exists for this availability value, use it; otherwise fall back to the base availability translation
        if (['outOfStock', 'notInThisRegion', 'notifyMe'].includes(availability.value))
            return te(`availability.${availability.value}.status`)
                ? t(`availability.${availability.value}.status`)
                : t(`availability.${availability.value}`);

        if (availability.value === 'commingSoon')
            return t('availability.comingsoon', {
                date: t('availability.weeks', { period: weekDifference.value === 0 ? '1-2' : `${weekDifference.value}-${weekDifference.value + 1}` }),
            });

        return t('availability.outOfStock.status');
    });

    return {
        availability,
        availabilityInfoText,
    };
};

export const useUspGallery = (productId: Ref<string | undefined | null>, usp: undefined | null | WidgetList<ProductUsp>) => {
    return computed(() => {
        if (!productId.value) return [];
        if (!usp) return [];

        return usp.sequence.reduce((accumulator: GalleryProps['data'], id) => {
            const comp = usp.components.find((p) => p.id === id);
            if (comp) {
                accumulator.push({
                    uspTitle: comp.title ?? '',
                    // uspSubtitle: comp.subtitle, // currently not used in gallery
                    uspText: comp.content ?? '',
                    src: getAssetUrl(`${productId.value}-${comp.code}`),
                    productBadge: comp.product_badge,
                    alt: comp.image_alt,
                    video: comp.video,
                });
            }
            return accumulator;
        }, []);
    });
};

export const useColorOption = (config: IConfigurable | undefined) => {
    const colorOption = computed(() => {
        return config?.options?.find((option) => option.type.includes('Color'));
    });
    const selectedColor = computed(() => colorOption.value?.selectedRef.value);
    const hoverColor = ref<string>('');

    const colorOptionChanged = (option: string) => {
        colorOption.value?.setSelected(option);
    };
    const changeHoverColorOption = (option: string) => {
        hoverColor.value = option;
    };
    return {
        colorOption,
        selectedColor,
        hoverColor,
        colorOptionChanged,
        changeHoverColorOption,
    };
};

export const useProductCard = (apiProduct: Partial<Product> | Partial<ProductPreviewContent>, initOptions?: ConfigurableProductInitOptions) => {
    const nuxtApp = useNuxtApp();
    const { ConfigurableProduct } = useEcommerce();
    const { addToCart, cartBusy } = useCart();
    const { isoLocale } = useLocales();

    const addToCartClicked = ref(false);

    const product = processApiProduct(apiProduct, isoLocale.value);
    const bikeDetail = useBikeDetail(apiProduct);
    const config = new ConfigurableProduct(product, initOptions);

    const variant = computed(() => {
        return config.selected.value;
    });

    const { colorOption, selectedColor, hoverColor, colorOptionChanged, changeHoverColorOption } = useColorOption(config);

    const toURL = computed(() => {
        return nuxtApp.$helpers.generateLocalePath(config.variantUrl?.value);
    });

    // TODO: Investigate why validate.limit reached does not update in handleAddToCart
    const handleAddToCart = (componentOrigin?: string) => {
        if (!variant.value) return;

        addToCartClicked.value = true;
        addToCart(
            [
                {
                    product,
                    variant: variant.value,
                    qty: 1,
                    attributes: {
                        _isBike: `${config?.isBike}`,
                    },
                },
            ],
            undefined,
            componentOrigin,
        ).finally(() => {
            addToCartClicked.value = false;
        });
    };

    const addToCartDisabled = computed(() => {
        if (!variant.value) return;
        return (cartBusy.value && !addToCartClicked) || variant.value.availabilityState === ProductAvailability.OutOfStock || !config.valid.value;
    });

    const hoverColorVariant = computed(() =>
        hoverColor.value ? product?.variants?.items?.find((variant) => variant.selectedOptions.Color === hoverColor.value) : undefined,
    );

    const isRedCharity = computed(() => {
        if (!variant.value) return false;
        return (variant.value.redCharity && !hoverColorVariant.value) || hoverColorVariant.value?.redCharity;
    });

    const currentImage = computed(() => {
        const sku = hoverColorVariant.value?.sku || variant.value?.sku;
        if (!sku) return;
        return getAssetUrl(`${sku}-side`);
    });

    return {
        addToCartClicked,
        addToCartDisabled,
        bikeDetail,
        cartBusy,
        changeHoverColorOption,
        colorOption,
        colorOptionChanged,
        config,
        currentImage,
        handleAddToCart,
        hoverColor,
        hoverColorVariant,
        isRedCharity,
        product,
        selectedColor,
        toURL,
        variant,
    };
};

export const useProductSummary = (requestData: Ref<PageProductSummaryFragment | null>) => {
    return {
        apiProduct: computed(() => requestData.value?.page?.product as Partial<Product> | undefined),
        title: computed(() => requestData.value?.page?.base?.title),
        description: computed(() => requestData.value?.page?.base?.description),
        baseUrl: computed(() => requestData.value?.url?.url),
    };
};
